import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { IAgencyModel } from '../models/account/IAgency.model';
import { IContractModel } from '../models/account/IContractModel';
import { IOrderModel, SortingProperty, TimePeriodsFilters } from '../models/account/IOrderModel';
import { Sorting } from '../models/account/sortingTypes.model';
import { IProfileConfig } from '../models/account/IProfile.model';
import { IShortWishlistModel } from '../models/account/IWishlist.model';
import { OrdersPayload } from '../actions/account/actions-orders';
import { compareByPropertyAsc, compareByPropertyDesc } from './utils/compare.utils';
import { MSubordinateList, ConfigService } from '@uxdf/direct-shared-lib';

@Injectable({
  providedIn: 'root'
})
export class BackendAccountService {
  component_url = {
    inventory: () => 'api/amadeus/inventory',
    contracts: () => 'api/amadeus/contracts',
    orders: () => 'api/amadeus/orders',
    profile: () => 'amadeus/private/customer/profile',
    updateProfile: store => `amadeus/private/customer`,
    agency: store => `rest/${store}/V1/amadeus/private/agency`,
    subordinates: agencyCAN => `api/amadeus/accountHierarchy/getSubordinates?can=${agencyCAN}`
  };

  constructor(
    private readonly httpClient: HttpClient,
    private readonly initService: ConfigService
  ) { }

  getSubordinates(agencyCAN): Observable<MSubordinateList> {
    return this.httpClient.get<MSubordinateList>(
      this.initService.getEnvConfig().AD_JAVA_API_URL + this.component_url.subordinates(agencyCAN),
      {
        responseType: 'json'
      });
  }

  getInventory(): Observable<any> {
    return this.httpClient.get<any>(this.initService.getEnvConfig().AD_JAVA_API_URL + this.component_url.inventory(), {
      responseType: 'json'
    });
  }

  getProfile(): Observable<any> {
    return this.httpClient.get<any>(this.initService.getEnvConfig().AD_MAGENTO_URL + this.component_url.profile(), {
      responseType: 'json'
    });
  }

  /* eslint-disable */
  updateProfile(customerUpdate, storeCode): Observable<Object> {
    return this.httpClient.put<IProfileConfig>(
      this.initService.getEnvConfig().AD_MAGENTO_URL + this.component_url.updateProfile(storeCode),
      {
        customerUpdate,
        isRegistration: false
      },
      {
        responseType: 'json'
      }
    );
  }
  getAgency(storeCode): Observable<IAgencyModel> {
    return this.httpClient.get<IAgencyModel>(this.initService.getEnvConfig().AD_MAGENTO_URL + this.component_url.agency(storeCode), {
      responseType: 'json'
    });
  }

  getContracts(): Observable<IContractModel[]> {
    return this.httpClient.get<IContractModel[]>(this.initService.getEnvConfig().AD_JAVA_API_URL + this.component_url.contracts(), {
      responseType: 'json'
    });
  }

  getOrders(): Observable<IOrderModel[]> {
    return this.httpClient.get<IOrderModel[]>(this.initService.getEnvConfig().AD_JAVA_API_URL + this.component_url.orders(), {
      responseType: 'json'
    });
  }

  getOrderPdf(url): any {
    return this.httpClient.get<any>(url, {
      responseType: 'arraybuffer' as 'json'
    });
  }
}

/* eslint-disable */
export function sortContracts(direction: Sorting, allContracts: IContractModel[]) {
  return direction === Sorting.asc || direction === Sorting.def
    ? allContracts.sort((a, b) => compareByPropertyAsc(a, b, 'fileDate'))
    : allContracts.sort((a, b) => compareByPropertyAsc(b, a, 'fileDate'));
}

/* eslint-disable */
export function sortWishlistsByWishlistId(direction: Sorting, allWishlists: IShortWishlistModel[]) {
  return direction === Sorting.asc || direction === Sorting.def
    ? allWishlists.sort((a, b) => compareByPropertyAsc(a, b, 'wishlist_id'))
    : allWishlists.sort((a, b) => compareByPropertyDesc(a, b, 'wishlist_id'));
}

/* eslint-disable */
export function filterWishlistsByAgentId(agentId: string, allWishlists: IShortWishlistModel[]) {
  const shownWishlists = allWishlists.map(wl => ({ ...wl }));
  const shownSortedWishlist = [];

  for (const value of shownWishlists) {
    value.isShown = +value.author_id === +agentId || +agentId === -1;
  }

  for (const value of shownWishlists) {
    if (value.isShown) {
      shownSortedWishlist.unshift(value);
    } else {
      shownSortedWishlist.push(value);
    }
  }

  return of(shownSortedWishlist);
}

/* eslint-disable */
export function sortWishlistsByWishlistDate(direction: Sorting, allWishlists: IShortWishlistModel[]) {
  return direction === Sorting.asc || direction === Sorting.def
    ? allWishlists.sort((a, b) => compareByPropertyAsc(a, b, 'updated_at'))
    : allWishlists.sort((a, b) => compareByPropertyDesc(a, b, 'updated_at'));
}

/* eslint-disable */
export function loadMoreWishlists(currentlyShown: number, wishlists: IShortWishlistModel[]) {
  const shownWishlists = [];
  for (let i = 0; i < wishlists.length && i < currentlyShown; i++) {
    const wl = { ...wishlists[i] };
    wl.isShown = true;
    shownWishlists.push(wl);
  }
  for (let i = currentlyShown; i < wishlists.length; i++) {
    const wl = { ...wishlists[i] };
    wl.isShown = false;
    shownWishlists.push(wl);
  }
  return shownWishlists;
}

/* eslint-disable */
export function getSearchedOrders(
  searchStr: string,
  allOrders: IOrderModel[],
  currentTimePeriod: TimePeriodsFilters,
  officesFilter: string
): OrdersPayload {
  const foundOrders = allOrders.filter(
    (order: IOrderModel) =>
      order.increment_id.indexOf(searchStr) > -1 || (order.status && order.status.indexOf(searchStr) > -1)
  );
  return {
    searchedOrders: foundOrders,
    allOrders,
    timePeriod: currentTimePeriod,
    officesFilter
  };
}

/* eslint-disable */
export function filterByDateAndOfficeCode(
  allOrders: IOrderModel[],
  dateType: TimePeriodsFilters,
  officesFilter: string
): Observable<OrdersPayload> {
  let searchedOrders: IOrderModel[] = [];

  if (dateType === TimePeriodsFilters.lastSixMonths) {
    searchedOrders = filterByHalfYearDate(allOrders);
  } else if (dateType === TimePeriodsFilters.currentYear) {
    searchedOrders = filterByCurrentYearDate(allOrders);
  } else if (dateType === TimePeriodsFilters.prevYear) {
    searchedOrders = filterByPrevYearDate(allOrders);
  }

  const foundOrders: IOrderModel[] = [];

  searchedOrders.forEach((order: IOrderModel) => {
    // empty office code to not skip magento orders, that have this empty field
    if (order.office_code === officesFilter || order.office_code === '') {
      foundOrders.push(order);
    }
  });

  return of({ allOrders, searchedOrders: foundOrders, timePeriod: dateType, officesFilter });
}

/* eslint-disable */
export function filterByHalfYearDate(allOrders: IOrderModel[]) {
  const foundOrders = [];
  allOrders.forEach(function (o) {
    const d1 = new Date();
    d1.setMonth(d1.getMonth() - 6);
    if (new Date(o.created_at) > d1) {
      foundOrders.push(o);
    }
  });
  return foundOrders;
}

/* eslint-disable */
export function filterByCurrentYearDate(allOrders: IOrderModel[]) {
  const foundOrders = [];
  allOrders.forEach(function (o) {
    if (new Date(o.created_at).getFullYear() === new Date().getFullYear()) {
      foundOrders.push(o);
    }
  });
  return foundOrders;
}

/* eslint-disable */
export function filterByPrevYearDate(allOrders: IOrderModel[]) {
  const foundOrders = [];
  allOrders.forEach(function (o) {
    if (new Date(o.created_at).getFullYear() === new Date().getFullYear() - 1) {
      foundOrders.push(o);
    }
  });
  return foundOrders;
}

/* eslint-disable */
export function compareByProperty(sortingDirection: Sorting, compareProperty: SortingProperty, orders: IOrderModel[]) {
  return sortingDirection === Sorting.asc || sortingDirection === Sorting.def
    ? orders.sort((a, b) => compareByPropertyAsc(a, b, compareProperty))
    : orders.sort((a, b) => compareByPropertyDesc(a, b, compareProperty));
}

function objectFlip(obj) {
  const ret = {};
  Object.keys(obj).forEach(key => {
    ret[obj[key]] = key;
  });
  return ret;
}
const mapToStateProperty = {
  sortingStatus: SortingProperty.OrderStatus,
  sortingDate: SortingProperty.CreationDate,
  sortingOrderNumber: SortingProperty.OrderNumber
};

/* eslint-disable */
export function resetSortingOfOtherProperties(sortingProperty: SortingProperty, sortingDirection: Sorting) {
  // create object with SortingProperty key and "State property" value
  // to make possible know, what state field dynamically change below
  const propsMap = objectFlip(mapToStateProperty);

  const resetedProps = {
    sortingStatus: Sorting.def,
    sortingDate: Sorting.def,
    sortingOrderNumber: Sorting.def
  };

  resetedProps[propsMap[sortingProperty]] = sortingDirection;

  return resetedProps;
}
